import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getAvailableDashboards } from '../utils/dashboardAccess';
import { getUserRoles } from '../utils/roleUtils';
import './HomeDashboardDropdown.css';

const HomeDashboardDropdown = ({ isOpen, onClose, position = { top: 0, left: 0 } }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useAuth();
  const dropdownRef = useRef(null);
  const [availableDashboards, setAvailableDashboards] = useState([]);

  // Load available dashboards for the user's roles (any role counts)
  const roleList = getUserRoles(user).join(',');
  useEffect(() => {
    if (roleList) {
      setAvailableDashboards(getAvailableDashboards(roleList.split(',')));
    }
  }, [roleList]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Handle dashboard selection
  const handleDashboardSelect = (dashboard) => {
    onClose(); // Close dropdown first

    // Navigate to selected dashboard
    if (dashboard.route) {
      navigate(dashboard.route);
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div className="dashboard-dropdown-overlay" onClick={onClose} />

      {/* Dropdown menu */}
      <div
        ref={dropdownRef}
        className="dashboard-dropdown-menu"
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          zIndex: 9999
        }}
      >
        <div className="dashboard-dropdown-header">
          <span className="dashboard-dropdown-icon">🏠</span>
          <span className="dashboard-dropdown-title">{t('homeDashboardDropdown.title')}</span>
        </div>

        <div className="dashboard-dropdown-options">
          {availableDashboards.map((dashboard) => (
            <button
              key={dashboard.code}
              className="dashboard-dropdown-option"
              onClick={() => handleDashboardSelect(dashboard)}
              title={t(`homeDashboardDropdown.dashboards.${dashboard.code}.description`, { defaultValue: dashboard.description })}
            >
              <span className="dashboard-option-icon">{dashboard.icon}</span>
              <div className="dashboard-option-content">
                <span className="dashboard-option-name">{t(`homeDashboardDropdown.dashboards.${dashboard.code}.name`, { defaultValue: dashboard.name })}</span>
                <span className="dashboard-option-description">{t(`homeDashboardDropdown.dashboards.${dashboard.code}.description`, { defaultValue: dashboard.description })}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="dashboard-dropdown-footer">
          <span className="dashboard-dropdown-hint">
            {t('homeDashboardDropdown.role')}: {getUserRoles(user).join(', ') || t('userInfo.unknown')}
          </span>
        </div>
      </div>
    </>
  );
};

export default HomeDashboardDropdown;